import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { StreamLanguage } from '@codemirror/language';
import { shell } from '@codemirror/legacy-modes/mode/shell';

import { useState } from 'react';

import { Code, CodeCard } from '@/components/Code/Code';
import { CopyButton } from '@/components/CopyButton/CopyButton';
import { Button, Text } from '@/design-system';
import { useNewApiKeyMutation } from '@/hooks/useSwr';
import { apiKeyPath } from '@/utils/constants';
import { getAPIUrl } from '@/utils/get-api-url';

import { Framework, SETUP_CODE_EXAMPLES } from './constants';

const shellLanguage = StreamLanguage.define(shell);

interface EnvQuickstartProps {
  projectName: string;
  setProjectName: (projectName: string) => void;
  framework?: Framework;
  lang: 'python' | 'typescript';
}

interface ConfigureEnvironmentProps {
  apiKey: string;
  apiUrl: string;
  projectName: string;
  setProjectName: (projectName: string) => void;
  idx: number;
}

export const CreateApiKey = (props: {
  newKey: string | null;
  setNewKey: (key: string | null) => void;
  idx?: number;
}) => {
  const createKey = useNewApiKeyMutation({});

  return (
    <div className="flex items-start gap-5 px-9 py-6">
      {props.idx && (
        <Text variant="h3" color="quaternary" weight="medium">
          {props.idx}
        </Text>
      )}
      <div className="flex items-center gap-2">
        {props.newKey ? (
          <div className="flex flex-col gap-2">
            <Text variant="h3" color="primary">
              New key created:
            </Text>
            <div className="flex items-center gap-2">
              <Text color="primary">{props.newKey} </Text>
              <CopyButton copy={props.newKey} />
            </div>
          </div>
        ) : (
          <>
            <Text variant="h3" color="primary">
              Create your API Key
            </Text>
            <Button
              size="sm"
              onClick={async () => {
                const resp = await createKey.trigger({
                  url: `${apiKeyPath}/current`,
                  json: {
                    description: 'Auto created during onboarding',
                  },
                });
                if (resp?.key) {
                  props.setNewKey(resp.key);
                }
              }}
              loading={createKey.isMutating}
            >
              Generate API Key
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const InstallDependencies = (props: {
  code: string;
  idx: number;
  lang: string;
}) => {
  return (
    <div className="flex gap-5 px-9 py-6">
      <Text variant="h3" color="quaternary" weight="medium">
        {props.idx}
      </Text>
      <div className="flex w-full flex-col gap-2">
        <Text variant="h3" color="primary">
          Install dependencies
        </Text>
        <CodeCard>
          <Code
            language={shellLanguage}
            value={props.code}
            readOnly
            showCopyButton
            variant="plain"
          />
        </CodeCard>
      </div>
    </div>
  );
};

const ConfigureEnvironment = (props: ConfigureEnvironmentProps) => {
  const envLines: string[] = [];
  envLines.push('export LANGSMITH_TRACING=true');
  envLines.push(`export LANGSMITH_ENDPOINT=${props.apiUrl}`);
  envLines.push(`export LANGSMITH_API_KEY=${props.apiKey}`);

  if (props.projectName !== undefined && props.projectName !== 'default') {
    envLines.push(`export LANGSMITH_PROJECT=${props.projectName}`);
  }
  envLines.push(`export OPENAI_API_KEY=<your-openai-api-key>`);
  const env = envLines.join('\n').trim();

  return (
    <div className="flex gap-4 px-9 py-6">
      <Text variant="h3" color="quaternary" weight="medium">
        {props.idx}
      </Text>

      <div className="flex w-full flex-col gap-2">
        <Text variant="h3" color="primary">
          Configure environment
        </Text>
        <CodeCard>
          <Code
            language={shellLanguage}
            value={env}
            readOnly
            showCopyButton
            variant="plain"
          />
        </CodeCard>
      </div>
    </div>
  );
};

const ExampleCode = (props: {
  code: string;
  lang: 'python' | 'typescript';
  idx: number;
  projectName: string;
}) => {
  return (
    <div className="flex gap-4 px-9 py-6">
      <Text variant="h3" color="quaternary" weight="medium">
        {props.idx}
      </Text>

      <div className="flex w-full grow flex-col gap-2">
        <Text variant="h3" color="primary">
          Run any agent, LLM, or chain. It will be traced to the project:{' '}
          {props.projectName}
        </Text>
        <CodeCard>
          <Code
            language={props.lang === 'python' ? python() : javascript()}
            value={props.code}
            readOnly
            showCopyButton
            omitMaxHeight
            variant="plain"
          />
        </CodeCard>
      </div>
    </div>
  );
};

const EnvQuickstart = ({
  projectName,
  setProjectName,
  framework = Framework.LangGraph,
  lang,
}: EnvQuickstartProps) => {
  const [newKey, setNewKey] = useState<string | null>(null);
  const apiUrl = getAPIUrl();
  const apiKey = newKey ?? '<your-api-key>';

  const installDependenciesCode =
    SETUP_CODE_EXAMPLES[framework][lang].installDependencies;
  const exampleCode = SETUP_CODE_EXAMPLES[framework][lang].exampleCode;

  return (
    <div className="flex flex-col items-stretch gap-2 divide-y divide-secondary ">
      <CreateApiKey newKey={newKey} setNewKey={setNewKey} idx={1} />
      <div className="relative">
        <InstallDependencies
          idx={2}
          code={installDependenciesCode}
          lang={lang}
        />
      </div>
      <ConfigureEnvironment
        apiKey={apiKey}
        apiUrl={apiUrl}
        projectName={projectName}
        setProjectName={setProjectName}
        idx={3}
      />
      <ExampleCode
        code={exampleCode}
        lang={lang}
        idx={4}
        projectName={projectName}
      />
    </div>
  );
};

export default EnvQuickstart;
